/**
 * Device-capability detection for UX adaptation (not security).
 *
 * Source-of-truth for "what platform is this user on, and what push
 * notification path is available?" — extracted 2026-05-16 from
 * src/components/shared/PWAInstallHint.jsx so the onboarding step-12
 * reminder+push combined screen can read the same signals.
 *
 * Conventions:
 *   - All checks are SSR-safe (return false when window/navigator absent)
 *   - User-agent sniffing is fine here because we're shaping UX, not gating
 *     security. Spoofed UAs would just see a non-ideal screen, not a leak.
 *   - One function per check; compose at the call site.
 */

/** True if the user is running iOS Safari (NOT Chrome/Firefox/Edge on iOS). */
export function isIOSSafari() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const isIOS = /iPad|iPhone|iPod/.test(ua)
  // Exclude Chrome (CriOS), Firefox (FxiOS), Edge (EdgiOS) on iOS — those
  // browsers can't install PWAs OR access Web Push on iOS.
  const isSafari = !/CriOS|FxiOS|EdgiOS/.test(ua)
  return isIOS && isSafari
}

/** True if running as an installed PWA (display-mode: standalone). */
export function isStandalone() {
  if (typeof window === 'undefined') return false
  // Legacy iOS API (predates standard matchMedia approach)
  if (window.navigator?.standalone === true) return true
  return window.matchMedia?.('(display-mode: standalone)').matches ?? false
}

/** True if running on any iOS device (Safari, Chrome, Firefox, Edge). */
export function isIOS() {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
}

/** True if running on an Android device. */
export function isAndroid() {
  if (typeof navigator === 'undefined') return false
  return /Android/i.test(navigator.userAgent)
}

/** True if running on any mobile device (iOS or Android). */
export function isMobile() {
  return isIOS() || isAndroid()
}

/**
 * Push-notification support level for the current device.
 *
 *   - 'works':              push works immediately on permission grant
 *   - 'needs_pwa_install':  iPhone Safari, not yet installed to home screen.
 *                           Permission API isn't available — user must add
 *                           to home screen first, then reopen from the icon.
 *   - 'unsupported':        browser doesn't support Notification + PushManager
 *                           at all (very rare on modern browsers)
 */
export function getPushSupportLevel() {
  if (typeof window === 'undefined') return 'unsupported'
  // iOS Safari pre-PWA: API is not exposed at all
  if (isIOSSafari() && !isStandalone()) return 'needs_pwa_install'
  // Generic capability check — covers desktop browsers + Android + iOS PWA
  const hasNotificationAPI = 'Notification' in window
  const hasPushAPI = 'PushManager' in window
  const hasServiceWorker = 'serviceWorker' in navigator
  if (!hasNotificationAPI || !hasPushAPI || !hasServiceWorker) return 'unsupported'
  return 'works'
}

/**
 * Friendly device-aware label for the Notifications option in onboarding
 * step 12. Caller can override (e.g., for testing other paths in admin
 * preview by passing a forced level).
 */
export function getNotificationsLabel(level = getPushSupportLevel()) {
  if (level === 'needs_pwa_install') return 'Phone push (needs home-screen install)'
  if (isMobile()) return 'Phone push'
  return 'Browser notifications'
}

/**
 * Short blurb describing what the user will get if they pick Notifications.
 * Device-aware, used as the live-preview line under the picker.
 */
export function getNotificationsBlurb(level = getPushSupportLevel()) {
  if (level === 'needs_pwa_install') {
    return 'Requires adding Illuminate to your home screen first.'
  }
  if (isMobile()) return 'A tap on your phone screen.'
  return 'A pop-up in your browser.'
}
